import { useMemo } from 'react';
import {
  Box,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { SystemJob } from 'src/Api/exchanges';

interface JobProgressStepperProps {
  job: SystemJob;
  /**
   * Noun shown after "Starting"/"Stopping" — e.g. "Unwinder", "Orderbooks",
   * "Everything".
   */
  label: string;
}

/**
 * Shared step-by-step progress view for an on/off `SystemJob`: a header with a
 * completion icon, a determinate progress bar while running, the vertical MUI
 * stepper, and the job-level error. Renders bare content (no Paper, no
 * positioning) so callers can drop it into a popover or a Dialog.
 *
 * Extracted from StopStartUnwinderButtons + ToggleUpdatesButton (which had
 * identical inline copies) and reused by the master StopAllButton.
 */
export const JobProgressStepper = ({ job, label }: JobProgressStepperProps) => {
  const progress = useMemo(() => {
    if (!job.steps.length) return 0;
    const completed = job.steps.filter(s => s.status === 'completed').length;
    return Math.round((completed / job.steps.length) * 100);
  }, [job]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5
        }}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {job.type === 'start' ? 'Starting' : 'Stopping'} {label}
        </Typography>
        {job.status === 'completed' && (
          <CheckCircleOutlineIcon
            sx={{ fontSize: 16, color: 'success.main' }}
          />
        )}
        {job.status === 'failed' && (
          <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
        )}
      </Box>

      {job.status === 'running' && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mb: 1, borderRadius: 1, height: 3 }}
        />
      )}

      <Stepper
        activeStep={job.currentStep}
        orientation="vertical"
        sx={{
          '& .MuiStepConnector-line': { minHeight: 8 },
          '& .MuiStepLabel-label': { fontSize: '0.7rem' },
          '& .MuiStepLabel-iconContainer': { pr: 1 },
          '& .MuiSvgIcon-root': { fontSize: '0.9rem' }
        }}
      >
        {job.steps.map((step, idx) => (
          <Step key={idx} completed={step.status === 'completed'}>
            <StepLabel error={step.status === 'failed'} sx={{ py: 0 }}>
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {job.error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, display: 'block' }}
        >
          {job.error}
        </Typography>
      )}
    </>
  );
};
